import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Platform, useColorScheme } from "react-native";

import { colors as lightColors, darkColors } from "@passfirst/ui";

import { log } from "./log";

// Theme context. Three layers of preference, top wins:
//   1. Explicit user choice ("light" | "dark") set via toggle
//   2. System preference (useColorScheme)
//   3. Light (always-safe default if neither resolves)
//
// Persistence: web localStorage (synchronous, hydrates immediately on
// mount) + native AsyncStorage (asynchronous, hydrates on the next
// tick after first render). The dual path means a returning user
// always sees their last picked theme — no flash of light mode on
// cold start once hydration completes.

export type Theme = "light" | "dark";
export type ThemePreference = Theme | "system";

// Widen literal hex types so both light + dark share the shape. Without
// this, `colors.brand` reads as the literal "#0F2A4A" in the light map
// and the same literal in dark — but TS infers the union as the dark
// version only since both happen to be "#0F2A4A". Other keys diverge
// (`background`: "#FAFAFB" vs "#0E1015") and the conditional return type
// won't narrow without the widen.
export type Colors = { [K in keyof typeof lightColors]: string };

const STORAGE_KEY = "passfirst.theme";

function isWeb(): boolean {
  return Platform.OS === "web";
}

function isPref(v: unknown): v is ThemePreference {
  return v === "light" || v === "dark" || v === "system";
}

// Web: synchronous read off localStorage so the first render lands on
// the persisted value (no theme flash). Returns null on native — that
// platform reads through persistGetAsync below on the next tick.
function persistGetSync(): ThemePreference | null {
  if (!isWeb() || typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  const v = window.localStorage.getItem(STORAGE_KEY);
  return isPref(v) ? v : null;
}

async function persistGetAsync(): Promise<ThemePreference | null> {
  // Web localStorage is faster than the AsyncStorage roundtrip; skip
  // the rehydrate effect there because persistGetSync already did it.
  if (isWeb()) return null;
  try {
    const v = await AsyncStorage.getItem(STORAGE_KEY);
    return isPref(v) ? v : null;
  } catch {
    return null;
  }
}

function persistSet(pref: ThemePreference): void {
  if (isWeb()) {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, pref);
    }
    return;
  }
  // Fire-and-forget on native — the in-memory state already updated;
  // a failed write just means the choice doesn't survive a cold start.
  void AsyncStorage.setItem(STORAGE_KEY, pref).catch((err) => {
    log.warn({ err: String(err) }, "theme pref persist failed");
  });
}

interface ThemeContextValue {
  /** Resolved theme actually in effect after layering preferences. */
  theme: Theme;
  colors: Colors;
  /** The preference the user has set, may be "system". */
  preference: ThemePreference;
  setPreference: (pref: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
  /** Initial value used before localStorage rehydrates (web). */
  initialPreference?: ThemePreference;
}

export function ThemeProvider({
  children,
  initialPreference = "system",
}: ProviderProps): JSX.Element {
  const systemScheme = useColorScheme();
  // Web reads synchronously so the first render is correct (no flash);
  // native falls through to "system" until the AsyncStorage effect
  // below resolves.
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => persistGetSync() ?? initialPreference,
  );

  // Native rehydrate. Web's already done it synchronously above.
  useEffect(() => {
    void (async () => {
      const persisted = await persistGetAsync();
      if (persisted) setPreferenceState(persisted);
    })();
  }, []);

  const setPreference = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref);
    persistSet(pref);
  }, []);

  const theme: Theme =
    preference === "system"
      ? systemScheme === "dark"
        ? "dark"
        : "light"
      : preference;

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      colors: theme === "dark" ? darkColors : lightColors,
      preference,
      setPreference,
    }),
    [theme, preference, setPreference],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside <ThemeProvider>");
  }
  return ctx;
}

/**
 * Convenience hook returning just the colors map for the active theme.
 * Most screens only need this — they `const c = useColors()` then style
 * via `backgroundColor: c.background` etc. The colors object has the
 * same shape in both themes, so existing call sites migrate by import
 * swap only.
 */
export function useColors(): Colors {
  return useTheme().colors;
}
